import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, LogOut, Save, Plus, Trash2, MousePointerClick, Mail, ArrowLeft, Copy, Download, Eye, Target } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ICON_OPTIONS = ["heart", "palette", "sparkles", "book-open", "award", "smile"];

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</Label>
      {children}
    </div>
  );
}

function ListEditor({ title, items, onChange, fields, newItem, testidPrefix }) {
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <section className="rounded-2xl border border-sand bg-white p-5 sm:p-6" data-testid={`${testidPrefix}-editor`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-olive">{title}</h2>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, { ...newItem }])} data-testid={`${testidPrefix}-add-button`}>
          <Plus className="mr-1 h-4 w-4" /> Aggiungi
        </Button>
      </div>
      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={i} className="space-y-3 rounded-xl border border-sand bg-cream p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-terracotta">Elemento {i + 1}</span>
              <button type="button" onClick={() => remove(i)} className="text-stone-400 hover:text-red-600" data-testid={`${testidPrefix}-remove-${i}`} aria-label="Rimuovi">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {fields.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.type === "textarea" ? (
                  <Textarea rows={3} value={item[f.key] ?? ""} onChange={(e) => update(i, { [f.key]: e.target.value })} data-testid={`${testidPrefix}-${i}-${f.key}`} />
                ) : f.type === "select" ? (
                  <select
                    value={item[f.key] ?? ""}
                    onChange={(e) => update(i, { [f.key]: e.target.value })}
                    data-testid={`${testidPrefix}-${i}-${f.key}`}
                    className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={item[f.key] ?? ""}
                    onChange={(e) => update(i, { [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                    data-testid={`${testidPrefix}-${i}-${f.key}`}
                  />
                )}
              </Field>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 rounded-3xl border border-sand bg-white p-8 shadow-lg" data-testid="admin-login-form">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta text-white">
            <Heart className="h-5 w-5 fill-current" />
          </span>
          <h1 className="font-display text-2xl font-bold text-olive">Area Autore</h1>
          <p className="text-sm text-stone-500">Accedi per aggiornare la pagina del tuo libro</p>
        </div>
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" placeholder="la-tua@email.it" />
        </Field>
        <Field label="Password">
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" placeholder="••••••••" />
        </Field>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-dark" data-testid="admin-login-submit-button">
          {loading ? "Accesso in corso..." : "Accedi"}
        </Button>
        <Link to="/" className="flex items-center justify-center gap-1 text-xs text-stone-400 hover:text-olive" data-testid="back-to-site-link">
          <ArrowLeft className="h-3 w-3" /> Torna alla pagina del libro
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    api.get("/content").then((r) => setContent(r.data));
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadData(); })
      .catch(() => setUser(false));
  }, []);

  const set = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Modifiche salvate! Sono già visibili sulla pagina pubblica.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="admin-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-terracotta" />
      </div>
    );
  }

  if (user === false) return <LoginForm onSuccess={(u) => { setUser(u); loadData(); }} />;

  if (!content) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="admin-content-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-terracotta" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-stone-200/60 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <span className="font-display text-lg font-bold text-olive" data-testid="admin-header-title">Area Autore</span>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site-link">
              <Button variant="outline" size="sm">Vedi la pagina</Button>
            </Link>
            <Button size="sm" onClick={save} disabled={saving} className="bg-terracotta hover:bg-terracotta-dark" data-testid="admin-save-button">
              <Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio..." : "Salva modifiche"}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button" aria-label="Esci">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
        {stats && (
          <div className="space-y-4" data-testid="admin-stats">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-sand bg-white p-5">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <Eye className="h-4 w-4 text-terracotta" /> Visite alla pagina
                </span>
                <p className="mt-2 font-display text-3xl font-extrabold text-olive" data-testid="stats-visits">{stats.visits}</p>
                <p className="mt-1 text-xs text-stone-400">Le tue visite da autrice non vengono contate</p>
              </div>
              <div className="rounded-2xl border border-sand bg-white p-5">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <MousePointerClick className="h-4 w-4 text-terracotta" /> Click su Amazon
                </span>
                <p className="mt-2 font-display text-3xl font-extrabold text-olive" data-testid="stats-amazon-clicks">{stats.amazon_clicks}</p>
                <p className="mt-1 text-xs text-stone-400">Persone che hanno premuto "Compra"</p>
              </div>
              <div className="rounded-2xl border border-sand bg-white p-5">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <Mail className="h-4 w-4 text-terracotta" /> Messaggi ricevuti
                </span>
                <p className="mt-2 font-display text-3xl font-extrabold text-olive" data-testid="stats-messages">{stats.messages}</p>
                <p className="mt-1 text-xs text-stone-400">Dal modulo "Scrivi all'autrice"</p>
              </div>
            </div>
            <div className="rounded-2xl border border-sand bg-white p-5" data-testid="goal-progress">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <Target className="h-4 w-4 text-terracotta" /> Obiettivo: 1000 copie al mese
                </span>
                <span className="font-display text-sm font-bold text-olive" data-testid="goal-percentage">
                  {Math.min(100, Math.round((stats.amazon_clicks / 1000) * 100))}%
                </span>
              </div>
              <div className="mt-3 h-3 overflow-hidden rounded-full bg-warm">
                <div
                  className="h-full rounded-full bg-terracotta transition-all duration-700"
                  style={{ width: `${Math.min(100, (stats.amazon_clicks / 1000) * 100)}%` }}
                  data-testid="goal-progress-bar"
                />
              </div>
              <p className="mt-2 text-xs text-stone-400">
                La barra si basa sui click verso Amazon misurati dal sito. Le vendite effettive le vedi nel tuo account Amazon KDP: ogni click è un potenziale acquirente.
              </p>
            </div>
          </div>
        )}

        <TelegramPanel />

        <section className="space-y-4 rounded-2xl border border-sand bg-white p-5 sm:p-6" data-testid="book-details-editor">
          <h2 className="font-display text-lg font-bold text-olive">Dettagli del libro</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titolo"><Input value={content.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title-input" /></Field>
            <Field label="Sottotitolo"><Input value={content.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="edit-subtitle-input" /></Field>
            <Field label="Autrice"><Input value={content.author} onChange={(e) => set("author", e.target.value)} data-testid="edit-author-input" /></Field>
            <Field label="Prezzo (es. €8,20)"><Input value={content.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-price-input" /></Field>
            <Field label="Link Amazon"><Input value={content.amazon_url} onChange={(e) => set("amazon_url", e.target.value)} data-testid="edit-amazon-url-input" /></Field>
            <Field label="URL immagine copertina"><Input value={content.cover_url} onChange={(e) => set("cover_url", e.target.value)} data-testid="edit-cover-url-input" /></Field>
            <Field label="Fascia d'età"><Input value={content.age_range} onChange={(e) => set("age_range", e.target.value)} data-testid="edit-age-range-input" /></Field>
            <Field label="Numero di pagine">
              <Input type="number" value={content.pages_count} onChange={(e) => set("pages_count", Number(e.target.value))} data-testid="edit-pages-count-input" />
            </Field>
          </div>
          <Field label="Descrizione (un paragrafo per riga vuota)">
            <Textarea rows={5} value={(content.description || []).join("\n\n")} onChange={(e) => set("description", e.target.value.split(/\n\s*\n/).filter(Boolean))} data-testid="edit-description-input" />
          </Field>
          <Field label="Cosa include (una voce per riga)">
            <Textarea rows={4} value={(content.includes || []).join("\n")} onChange={(e) => set("includes", e.target.value.split("\n").filter(Boolean))} data-testid="edit-includes-input" />
          </Field>
          <Field label="Biografia / lettera dell'autrice">
            <Textarea rows={4} value={content.author_bio} onChange={(e) => set("author_bio", e.target.value)} data-testid="edit-author-bio-input" />
          </Field>
        </section>

        <ListEditor
          title="Pagine dell'estratto gratuito"
          items={content.excerpt_pages || []}
          onChange={(v) => set("excerpt_pages", v)}
          newItem={{ title: "", text: "", image_url: "" }}
          testidPrefix="excerpt"
          fields={[
            { key: "title", label: "Titolo della pagina" },
            { key: "image_url", label: "URL immagine" },
            { key: "text", label: "Testo", type: "textarea" },
          ]}
        />

        <ListEditor
          title="Benefici"
          items={content.benefits || []}
          onChange={(v) => set("benefits", v)}
          newItem={{ icon: "heart", title: "", text: "" }}
          testidPrefix="benefit"
          fields={[
            { key: "icon", label: "Icona", type: "select", options: ICON_OPTIONS },
            { key: "title", label: "Titolo" },
            { key: "text", label: "Testo", type: "textarea" },
          ]}
        />

        <ListEditor
          title="Recensioni"
          items={content.testimonials || []}
          onChange={(v) => set("testimonials", v)}
          newItem={{ name: "", role: "", text: "", stars: 5 }}
          testidPrefix="testimonial"
          fields={[
            { key: "name", label: "Nome" },
            { key: "role", label: "Ruolo (es. Mamma di...)" },
            { key: "text", label: "Recensione", type: "textarea" },
            { key: "stars", label: "Stelle (1-5)", type: "number" },
          ]}
        />

        <ListEditor
          title="FAQ"
          items={content.faq || []}
          onChange={(v) => set("faq", v)}
          newItem={{ question: "", answer: "" }}
          testidPrefix="faq"
          fields={[
            { key: "question", label: "Domanda" },
            { key: "answer", label: "Risposta", type: "textarea" },
          ]}
        />

        <section className="rounded-2xl border border-sand bg-white p-5 sm:p-6" data-testid="messages-list">
          <h2 className="mb-4 font-display text-lg font-bold text-olive">Messaggi ricevuti</h2>
          {messages.length === 0 ? (
            <p className="text-sm text-stone-500" data-testid="messages-empty">Nessun messaggio per ora.</p>
          ) : (
            <ul className="space-y-3">
              {messages.map((m, i) => (
                <li key={i} className="rounded-xl border border-sand bg-cream p-4" data-testid={`message-item-${i}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-olive">{m.name}</span>
                    <span className="text-xs text-stone-500">{m.email}</span>
                  </div>
                  <p className="mt-2 text-sm text-stone-600">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-sand bg-white p-5 sm:p-6" data-testid="promo-kit">
          <h2 className="font-display text-lg font-bold text-olive">Kit Passaparola</h2>
          <p className="mt-1 text-sm text-stone-500">
            Testi pronti da copiare e incollare nei gruppi Facebook e WhatsApp di genitori e insegnanti, più due immagini già pronte da allegare al post.
          </p>
          <div className="mt-5 space-y-4">
            {[
              {
                label: "Per i gruppi di genitori",
                text: `Ciao a tutte/i! Vi segnalo un quaderno operativo per bambini dai 6 agli 11 anni che aiuta a riconoscere ed esprimere le emozioni: 20 attività creative, diario delle emozioni e certificato finale. Lo ha creato Valentina Maritato, esperta in intelligenza emotiva e facilitatrice mindfulness. Sul sito trovate l'estratto gratuito e una scheda omaggio da stampare: ${typeof window !== "undefined" ? window.location.origin : ""} — Il libro è su Amazon a 10€.`,
              },
              {
                label: "Per insegnanti ed educatori",
                text: `Ciao colleghe e colleghi! Condivido uno strumento utile per l'educazione emotiva in classe: "Io Emozione – Quaderno Operativo" (6-11 anni), con 20 schede operative pronte all'uso per riconoscere ed esprimere le emozioni. Nasce dai progetti scolastici di Valentina Maritato (Io Emozione, EmoProject, Sinfonie – Accordo di Suoni). Idoneo Carta del Docente. Estratto gratuito qui: ${typeof window !== "undefined" ? window.location.origin : ""}`,
              },
              {
                label: "Per nonni e famiglia (idea regalo)",
                text: `Un'idea regalo diversa dal solito per nipoti dai 6 agli 11 anni: "Io Emozione", un quaderno di attività creative che insegna ai bambini a conoscere e raccontare le proprie emozioni. Si fa insieme, pagina dopo pagina. Lo trovate su Amazon a 10€ e qui c'è un assaggio gratuito: ${typeof window !== "undefined" ? window.location.origin : ""}`,
              },
            ].map((post, i) => (
              <div key={i} className="rounded-xl border border-sand bg-cream p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-terracotta">{post.label}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(post.text).then(() => toast.success("Testo copiato! Incollalo nel gruppo.")).catch(() => toast.error("Copia non riuscita"))}
                    data-testid={`promo-kit-copy-${i}`}
                  >
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copia testo
                  </Button>
                </div>
                <p className="text-sm leading-relaxed text-stone-600">{post.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-sand bg-cream p-4">
              <img src="/files/social-card-genitori.png" alt="Immagine social per genitori" className="w-full rounded-lg border border-sand" />
              <a href="/files/social-card-genitori.png" download="io-emozione-post-genitori.png" data-testid="promo-kit-download-genitori" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta hover:text-terracotta-dark">
                <Download className="h-4 w-4" /> Scarica immagine per genitori
              </a>
            </div>
            <div className="rounded-xl border border-sand bg-cream p-4">
              <img src="/files/social-card-maestre.png" alt="Immagine social per insegnanti" className="w-full rounded-lg border border-sand" />
              <a href="/files/social-card-maestre.png" download="io-emozione-post-maestre.png" data-testid="promo-kit-download-maestre" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta hover:text-terracotta-dark">
                <Download className="h-4 w-4" /> Scarica immagine per insegnanti
              </a>
            </div>
          </div>
          <div className="mt-5 rounded-xl border border-terracotta/30 bg-terracotta-light/40 p-4" data-testid="ads-kit">
            <p className="font-display text-sm font-bold text-olive">Kit Pubblicità — per "Metti in evidenza" e Gestione Inserzioni</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">
              4 annunci da testare (scaricali e usali come immagine della pubblicità) + il carosello in 5 immagini, già pubblicato sul tuo Instagram e pronto da promuovere.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              {[
                ["annuncio-A-rabbia.jpg", "Annuncio A · La rabbia (genitori)"],
                ["annuncio-B-parlare.jpg", "Annuncio B · Far parlare i bambini"],
                ["annuncio-C-esperta.jpg", "Annuncio C · La tua esperienza"],
                ["annuncio-D-regalo.jpg", "Annuncio D · Idea regalo"],
                ["carosello-1.jpg", "Carosello · immagine 1 (aggancio)"],
                ["carosello-2.jpg", "Carosello · immagine 2"],
                ["carosello-3.jpg", "Carosello · immagine 3"],
                ["carosello-4.jpg", "Carosello · immagine 4"],
                ["carosello-5.jpg", "Carosello · immagine 5 (offerta)"],
              ].map(([file, label], i) => (
                <a key={file} href={`/files/ads/${file}`} download={file} data-testid={`ads-kit-download-${i}`} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 font-medium text-terracotta hover:text-terracotta-dark">
                  <Download className="h-3.5 w-3.5 shrink-0" /> {label}
                </a>
              ))}
            </div>
          </div>
        </section>

        <div className="flex justify-center pb-8">
          <Button onClick={save} disabled={saving} className="bg-terracotta hover:bg-terracotta-dark" data-testid="admin-save-button-bottom">
            <Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </div>
      </main>
    </div>
  );
}

function TelegramPanel() {
  const [tg, setTg] = useState(null);
  const [ig, setIg] = useState(null);
  const [fb, setFb] = useState(null);
  const [publishing, setPublishing] = useState("");

  const load = () => {
    api.get("/telegram/status").then((r) => setTg(r.data)).catch(() => {});
    api.get("/instagram/status").then((r) => setIg(r.data)).catch(() => {});
    api.get("/facebook/status").then((r) => setFb(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const publish = async (which) => {
    setPublishing(which);
    try {
      const url = which.endsWith("-story") ? `/${which.replace("-story", "")}/publish-story` : `/${which}/publish`;
      const { data } = await api.post(url);
      if (data.ok) {
        toast.success(`Pubblicato: "${data.title}"`);
      } else {
        toast.error("La piattaforma non ha accettato il post. Riprova tra poco.");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPublishing("");
    }
  };

  const channels = [
    tg?.configured && { key: "telegram", name: "Canale Telegram", handle: tg.channel, schedule: tg.schedule, posts: tg.posts, color: "bg-[#229ED9] hover:bg-[#1b8ac0]", testid: "telegram", story: false },
    ig?.configured && { key: "instagram", name: "Instagram", handle: `@${ig.username}`, schedule: ig.schedule, posts: ig.posts, color: "bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 hover:opacity-90", testid: "instagram", story: true },
    fb?.configured && { key: "facebook", name: "Pagina Facebook", handle: fb.page, schedule: fb.schedule, posts: fb.posts, color: "bg-[#1877F2] hover:bg-[#0f66d8]", testid: "facebook", story: true },
  ].filter(Boolean);

  if (!channels.length) return null;

  return (
    <section className="rounded-2xl border border-sand bg-white p-5 sm:p-6" data-testid="telegram-section">
      <h2 className="font-display text-lg font-bold text-olive">Pubblicazione automatica social</h2>
      <p className="mt-1 text-sm text-stone-500">
        I canali collegati pubblicano da soli ogni giorno alle 18:00. I pulsanti pubblicano subito il prossimo contenuto della scaletta.
      </p>
      <div className="mt-5 space-y-5">
        {channels.map((c) => (
          <div key={c.key} className="rounded-xl border border-sand bg-cream p-4" data-testid={`${c.testid}-panel`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="font-display text-base font-bold text-olive">{c.name}</span>
                <span className="ml-2 text-sm font-semibold text-terracotta">{c.handle}</span>
                <p className="mt-0.5 text-xs text-stone-500">Automatico: {c.schedule}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => publish(c.key)} disabled={publishing === c.key} className={c.color} data-testid={`${c.testid}-publish-button`}>
                  {publishing === c.key ? "Pubblicazione..." : "Pubblica ora"}
                </Button>
                {c.story && (
                  <Button variant="outline" onClick={() => publish(`${c.key}-story`)} disabled={publishing === `${c.key}-story`} data-testid={`${c.testid}-story-publish-button`}>
                    {publishing === `${c.key}-story` ? "Pubblicazione..." : "Pubblica storia"}
                  </Button>
                )}
              </div>
            </div>
            {c.posts?.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid={`${c.testid}-posts-list`}>
                {c.posts.map((p, i) => (
                  <li key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-1.5 text-sm" data-testid={`${c.testid}-post-${i}`}>
                    <span className="font-medium text-olive">{p.title}</span>
                    <span className="text-xs text-stone-400">
                      {p.source} · {new Date(p.created_at).toLocaleString("it-IT")} · {p.ok ? "pubblicato" : "errore"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
